import i18n from 'i18next';
import {DataStatus, Pagination} from 'platform/components';
import {Link, Show, Text, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {suffixTestId, type TestIdProps} from 'shared/web';

import type {EntityResourceResolver} from '../../context/EntityResourceResolver';
import {type BulkOperationItem, type BulkOperationItemState} from '../../types/api/bulkOperations';
import type {BulkOperationPresentation} from '../../types/BulkOperationPresentation';
import type {BulkOperationsServices} from '../../types/BulkOperationsServices';
import {getBulkOperationRecordLabel} from '../../utils/getBulkOperationRecordLabel';

const PAGE_SIZE = 25;

type BulkOperationItemsProps = TestIdProps & {
  services: BulkOperationsServices;
  entityResourceResolver?: EntityResourceResolver;
  bulkOperationId: string;
  count: number;
  resourceId: string;
  state: BulkOperationItemState;
  title: string;
  successMessage?: string;
};

function getItemReason(item: BulkOperationItem): string {
  // Item errorMessage is the API's user-facing failure/skip reason; React renders it as text.
  return (
    item.errorMessage ??
    i18n.t(
      item.state === 'SKIPPED'
        ? 'entity.automationRule.detail.runResult.skipped'
        : 'general.notifications.labels.failed'
    )
  );
}

function BulkOperationRecordLink(props: {
  item: BulkOperationItem;
  resourceId: string;
  entityResourceResolver?: EntityResourceResolver;
  presentation?: BulkOperationPresentation;
}) {
  const label = getBulkOperationRecordLabel(props.resourceId, props.item, props.presentation);
  const path = props.item.recordAvailable
    ? props.entityResourceResolver?.(props.resourceId, props.item.recordId)
    : undefined;

  return (
    <Text size="small" color="secondary">
      {path === undefined ? (
        <Text inline size="small" alternative>
          {label.linkLabel}
        </Text>
      ) : (
        <Link href={path} title={label.linkLabel} size="small" />
      )}
      <Show when={isNotNilOrEmpty(label.description) ? label.description : null}>
        {(description) => (
          <Text inline size="small" color="secondary">
            {' '}
            · {description}
          </Text>
        )}
      </Show>
    </Text>
  );
}

export function BulkOperationItems(props: BulkOperationItemsProps) {
  const [page, setPage] = useState(1);
  const {data, isLoading, isError} = props.services.useItems(
    {
      bulkOperationId: props.bulkOperationId,
      state: props.state,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    },
    {skip: props.count === 0}
  );

  if (props.count === 0) {
    return (
      <Text size="small" alternative>
        {i18n.t('general.labels.noData')}
      </Text>
    );
  }

  const pagesQuantity = Math.max(1, Math.ceil(props.count / PAGE_SIZE));

  return (
    <VStack spacing={3} data-testid={suffixTestId(props.state.toLowerCase(), props)}>
      <h3 className="sr-only">{`${props.title} (${props.count})`}</h3>
      <DataStatus isLoading={isLoading} isError={isError} minHeight={16}>
        <ul className="overflow-hidden rounded-md border! border-solid! border-separator">
          {data?.map((item) => (
            <li
              key={item.recordId}
              className="border-b! border-solid! border-separator px-4 py-3 last:border-b-0!"
            >
              <VStack spacing={1}>
                <BulkOperationRecordLink
                  presentation={props.services.presentation}
                  entityResourceResolver={props.entityResourceResolver}
                  item={item}
                  resourceId={props.resourceId}
                />
                <Text size="small" color="secondary">
                  {item.state === 'DONE'
                    ? (props.successMessage ?? i18n.t('general.notifications.success'))
                    : getItemReason(item)}
                </Text>
              </VStack>
            </li>
          ))}
        </ul>
      </DataStatus>
      <Show when={pagesQuantity > 1}>
        <Pagination pagesQuantity={pagesQuantity} page={page} onPageChange={setPage} />
      </Show>
    </VStack>
  );
}
